import type { RJSFSchema, UiSchema } from '@rjsf/utils';
import type { ReactElement } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import ErrorState from 'app/shared-components/common/ErrorState';

import { CONTENT_TABS, websiteDetailsPath } from 'app/pages/PlusWebsites/paths';
import { useOrganisation } from 'app/pages/PlusWebsites/hooks/useOrganisation';

import { useGetSectionTypeVersion } from '../../../Pages/Section/Create/hooks/useGetSectionTypeVersion';
import { WebsiteSectionEditor } from '../../components/WebsiteSectionEditor';
import { useCreateWebsiteSection } from '../../hooks/useCreateWebsiteSection';
import { prefillFromOrganisation } from './organisationPrefill';

interface LocationState {
  sectionDisplayName?: string;
  sectionTypeId?: string;
  sectionTypeVersion?: number;
}

const CreateWebsiteSectionPage = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const websiteId = searchParams.get('websiteId') ?? '';
  const organisationId = searchParams.get('organisationId') ?? '';
  const location = useLocation();
  const navigate = useNavigate();
  const {
    sectionDisplayName = 'Section',
    sectionTypeId = '',
    sectionTypeVersion = 1,
  } = (location.state as LocationState | null) ?? {};

  const { sectionTypeData, isLoading, isError, error } = useGetSectionTypeVersion({
    id: sectionTypeId,
    version: sectionTypeVersion,
  });

  // Presets the contact fields from what the pharmacy has already told us. The editor holds the
  // form back until this settles: rjsf takes initial data on the first render only.
  const { organisation, isLoading: isOrganisationLoading } = useOrganisation(organisationId);

  const { mutateAsync: createWebsiteSectionAsync, isPending } = useCreateWebsiteSection();

  if (!websiteId || !sectionTypeId) {
    return (
      <ErrorState
        title="Missing required parameters"
        subtitle="Cannot create section without valid website and section type information"
      />
    );
  }

  if (isError) {
    return (
      <ErrorState
        title="Failed to load section details"
        subtitle={error instanceof Error ? error.message : 'Try again later'}
      />
    );
  }

  // Returns the promise so the editor can await the save before clearing its unsaved state.
  const handleSubmit = (data: Record<string, unknown>) =>
    createWebsiteSectionAsync(
      { websiteId, sectionTypeId, version: sectionTypeVersion, data },
      {
        // The form is spent once the section exists, so it takes the place of the create
        // entry rather than leaving it behind for the back button to find.
        onSuccess: () =>
          navigate(websiteDetailsPath(searchParams, CONTENT_TABS.headerAndFooter), {
            replace: true,
          }),
      }
    );

  const schema = (sectionTypeData?.jsonSchema ?? {}) as RJSFSchema;

  return (
    <WebsiteSectionEditor
      title={`Add ${sectionDisplayName}`}
      schema={schema}
      uiSchema={(sectionTypeData?.uiSchema ?? {}) as UiSchema}
      formData={prefillFromOrganisation(schema, organisation)}
      isLoading={isLoading || isOrganisationLoading}
      isSaving={isPending}
      isNew
      onSubmit={handleSubmit}
    />
  );
};

export default CreateWebsiteSectionPage;
